import { supabase } from '../lib/supabase';

export const dealService = {
  /**
   * Get all deals for current tenant
   * @returns {Promise<Array>} Array of deal objects
   */
  async getAll() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('tenant_id')
        ?.eq('id', user?.id)
        ?.single();

      const { data, error } = await supabase
        ?.from('deals')
        ?.select('*')
        ?.eq('tenant_id', profile?.tenant_id)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(deal => ({
        id: deal?.id,
        tenantId: deal?.tenant_id,
        title: deal?.title,
        value: deal?.value,
        stage: deal?.stage,
        probability: deal?.probability,
        expectedCloseDate: deal?.expected_close_date,
        assignedTo: deal?.assigned_to,
        tags: deal?.tags,
        createdAt: deal?.created_at,
        updatedAt: deal?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching deals:', error);
      throw error;
    }
  },

  /**
   * Create new deal
   * @param {Object} dealData - Deal data to create
   * @returns {Promise<Object>} Created deal object
   */
  async create(dealData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('tenant_id')
        ?.eq('id', user?.id)
        ?.single();

      const { data, error } = await supabase
        ?.from('deals')
        ?.insert({
          tenant_id: profile?.tenant_id,
          created_by: user?.id,
          title: dealData?.title,
          description: dealData?.description,
          value: dealData?.value || 0,
          stage: dealData?.stage || 'new',
          probability: dealData?.probability || 0,
          expected_close_date: dealData?.expectedCloseDate,
          assigned_to: dealData?.assignedTo || user?.id,
          tags: dealData?.tags || [],
          notes: dealData?.notes,
          is_active: true
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        tenantId: data?.tenant_id,
        title: data?.title,
        value: data?.value,
        stage: data?.stage,
        probability: data?.probability,
        expectedCloseDate: data?.expected_close_date,
        assignedTo: data?.assigned_to,
        tags: data?.tags,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating deal:', error);
      throw error;
    }
  },

  /**
   * Update existing deal
   * @param {string} dealId - Deal ID to update
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated deal object
   */
  async update(dealId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('deals')
        ?.update({
          title: updates?.title,
          description: updates?.description,
          value: updates?.value,
          stage: updates?.stage,
          probability: updates?.probability,
          expected_close_date: updates?.expectedCloseDate,
          assigned_to: updates?.assignedTo,
          tags: updates?.tags,
          notes: updates?.notes,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', dealId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        title: data?.title,
        value: data?.value,
        stage: data?.stage,
        probability: data?.probability,
        expectedCloseDate: data?.expected_close_date
      };
    } catch (error) {
      console.error('Error updating deal:', error);
      throw error;
    }
  },

  /**
   * Delete deal
   * @param {string} dealId - Deal ID to delete
   * @returns {Promise<void>}
   */
  async delete(dealId) {
    try {
      const { error } = await supabase
        ?.from('deals')
        ?.delete()
        ?.eq('id', dealId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting deal:', error);
      throw error;
    }
  },

  /**
   * Get filtered deals with date range, stage, and user filters
   * @param {Object} filters - Filter parameters
   * @param {string} filters.startDate - Start date (ISO string)
   * @param {string} filters.endDate - End date (ISO string)
   * @param {Array<string>} filters.stages - Array of stage values to filter by
   * @param {string} filters.assignedTo - User ID to filter by
   * @returns {Promise<Array>} Array of filtered deal objects
   */
  async getFilteredDeals(filters = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('tenant_id')
        ?.eq('id', user?.id)
        ?.single();

      let query = supabase
        ?.from('deals')
        ?.select('*')
        ?.eq('tenant_id', profile?.tenant_id);

      // Apply date range filter
      if (filters?.startDate) {
        query = query?.gte('created_at', filters?.startDate);
      }
      if (filters?.endDate) {
        query = query?.lte('created_at', filters?.endDate);
      }

      // Apply stage filter
      if (filters?.stages && filters?.stages?.length > 0) {
        query = query?.in('stage', filters?.stages);
      }

      // Apply assigned user filter
      if (filters?.assignedTo && filters?.assignedTo !== 'all') {
        query = query?.eq('assigned_to', filters?.assignedTo);
      }

      query = query?.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(deal => ({
        id: deal?.id,
        tenantId: deal?.tenant_id,
        title: deal?.title,
        value: deal?.value,
        stage: deal?.stage,
        probability: deal?.probability,
        expectedCloseDate: deal?.expected_close_date,
        actualCloseDate: deal?.actual_close_date,
        assignedTo: deal?.assigned_to,
        tags: deal?.tags,
        createdAt: deal?.created_at,
        updatedAt: deal?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching filtered deals:', error);
      throw error;
    }
  }
};